import { Enquiry } from "../../components/Enquiry";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import LogoMarquee from "@/components/Sections/LogoMarquee";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { Card, Carousel } from "@/components/layout/sections/CardCarousel";
import { FeatureShowcase } from "@/components/layout/sections/FeatureShowcase";
import { Hero } from "@/components/layout/sections/Hero";
import { ProductFooter } from "@/components/layout/sections/ProductFooter";
import Section from "@/components/layout/sections/Section";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faBookFont, faCirclePlus, faLayerGroup, faStar } from "@icons/regular";
import { Button } from "@ui";
import { Link, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const brandingFeatures = [
  {
    id: 1,
    title: "Theme Builder",
    description:
      "Use Theme Builder to lock in your fonts, colours, and logo. Set global styles to align everything with brand guidelines.",
    imageUrl: "/product/branding/theme-builder.png",
    imageAlt: "Theme Builder interface showing options for customizing typography and color palette.",
  },
  {
    id: 2,
    title: "Standard themes",
    description:
      "Not sure where to start? Save time with curated standard themes that provide a polished, professional starting point for your stories.",
    imageUrl: "/product/branding/standard-themes.png",
    imageAlt: "Theme selection interface showing a variety of pre-designed themes to choose from.",
  },
  {
    id: 3,
    title: "Advanced customisation",
    description:
      "Apply custom JavaScript, HTML, and CSS for full control over layout and styles. Integrate third-party tools for analytics, tracking, cookie consent, tag management, and more.",
    imageUrl: "/product/branding/advanced-customisation.png",
    imageAlt: "Code editor interface showing custom code snippets.",
  },
];

const FeaturesBranding: React.FC<{ location: any; data: any }> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const { allFeaturedStoriesJson } = props.data;
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    setShowPortal(true);
    prePopulatedFields.current = fields || {};
  }

  const cards = allFeaturedStoriesJson.nodes.map((story: any, index: number) => (
    <Card
      key={story.linkUrl || index}
      card={{
        id: index + 1,
        title: story.title,
        imageUrl: story.imageUrl?.src?.publicURL || story.imageUrl?.src?.childImageSharp?.gatsbyImageData?.images?.fallback?.src || "",
        linkUrl: story.linkUrl,
      }}
    />
  ));

  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  return (
    <Layout location={props.location} theme="dark-theme">
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-features body-light hide-elevio",
          }}
        ></Helmet>
        <Hero
          title={
            <>
              Stay true to your <span className="tw-font-PassengerSerif tw-italic">brand</span>.
            </>
          }
          description="Effortlessly on brand, every time. Whether you’re a solo creator, growing your team, or collaborating in an enterprise environment, every story you publish with Shorthand will look and feel distinctly yours."
          image={
            <StaticImage
              src={`../../../static/product/branding/branding-hero.png`}
              alt="Photo collage of Shorthand customer stories showing different branding styles"
              width={2432}
              height={1442}
              className="tw-w-[76rem] tw-rounded-md tw-bg-white/5 tw-shadow-2xl tw-ring-1 tw-ring-white/10"
            />
          }
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />

        <Section>
          <TitleSection headingElement="h2" title="Trusted by the world’s leading brands" />
          <LogoMarquee />
        </Section>

        <Section>
          <FeatureShowcase
            preventOverflow
            title={
              <>
                Your brand, <span className="tw-font-PassengerSerif tw-italic">built in</span>
              </>
            }
            description={"Collaborate with Shorthand's expert team to craft bespoke custom themes."}
            features={[
              {
                name: "Standardise colours, fonts, and typography across stories",
                icon: faBookFont,
              },
              {
                name: "Apply logos, buttons, and whole sections with brand consistency",
                icon: faCirclePlus,
              },
              {
                name: "Ensure content stays visually cohesive across your team and organisation",
                icon: faLayerGroup,
              },
              {
                name: "Build custom section templates to accelerate production timelines",
                icon: faStar,
              },
            ]}
            image="product/branding/built-in-brand.png"
            imagePlacement="left"
          />
        </Section>

        <Section>
          <div className="tw-mt-10 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-mt-16 sm:tw-grid-cols-1 lg:tw-grid-cols-3">
            {brandingFeatures.map(feature => (
              <div key={feature.id} className="tw-relative">
                <div className="tw-absolute tw-inset-px tw-rounded-lg tw-bg-grey_scale-800/50" />
                <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-[calc(theme(borderRadius.lg)+1px)]">
                  <div className="tw-px-4 tw-pb-1 tw-pt-4 sm:tw-px-10 sm:tw-pb-0 sm:tw-pt-10">
                    <p className="tw-mt-2 !tw-text-lg tw-font-medium tw-tracking-tight tw-text-[var(--foreground-color)] default-max-lg:tw-text-center">
                      {feature.title}
                    </p>
                    <p className="tw-mt-2 tw-max-w-lg !tw-text-base tw-text-[var(--foreground-color-muted)] default-max-lg:tw-text-center">
                      {feature.description}
                    </p>
                  </div>
                  <div className="tw-flex-grow"></div>
                  <div className="tw-relative tw-mt-4">
                    <div className="tw-relative tw-ml-4 tw-mr-0 tw-overflow-hidden tw-rounded-tl-xl tw-bg-grey_scale-800/50 tw-shadow-2xl tw-outline tw-outline-white/10 sm:tw-ml-10">
                      <div className="tw-relative tw-aspect-video">
                        <img
                          alt={feature.imageAlt}
                          src={feature.imageUrl}
                          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-center"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-pointer-events-none tw-absolute tw-inset-px tw-rounded-lg tw-shadow tw-outline tw-outline-black/5" />
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                Where great <span className="tw-font-PassengerSerif tw-italic">brands</span> build
              </>
            }
            description="Some of the world’s most iconic brands use Shorthand. Get inspired by how leading global teams craft powerful stories with Shorthand."
          />
          <Carousel items={cards} />
        </Section>

        <ProductFooter />
        <Footer className="mt-0" />
      </main>
    </Layout>
  );
};

export default FeaturesBranding;

export const query = graphql`
  query FeaturesBrandingQuery {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
    allFeaturedStoriesJson {
      nodes {
        title
        linkUrl
        imageUrl {
          alt
          src {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, width: 600, height: 830, quality: 80)
            }
            publicURL
          }
        }
      }
    }
  }
`;
